import React                          from 'react';
import View                           from '@widesk-ui/components/View';
import SettingsButton                 from '@/TokamapEditor/ux/AreaSidebar/SettingsPopover';
import Segmented                      from '@widesk-ui/Segmented';
import { observer }                   from 'mobx-react-lite';
import { type Shape }                 from '@tokamap/core';
import { type ShapeSurface }          from '@tokamap/core';
import { type SurfaceMaterial }       from '@tokamap/core';
import { type SurfaceScale }          from '@tokamap/core';
import { type SurfaceBorder }         from '@tokamap/core';
import { type SurfaceScatterDensity } from '@tokamap/core';
import { type RailingStyle }          from '@tokamap/core';
import { SURFACE_MATERIALS }          from '@tokamap/core';
import { SURFACE_BORDERS }            from '@tokamap/core';
import { SCATTERED_MATERIALS }        from '@tokamap/core';
import { RAILING_STYLES }             from '@tokamap/core';
import { surfacePreviewUrl }          from '@tokamap/core';
import { surfaceBorderPreviewUrl }    from '@tokamap/core';
import { railingPreviewUrl }          from '@tokamap/core';
import Field                          from '@/TokamapEditor/ux/AreaSidebar/Field';
import { FieldLabelSuffix }           from '@/TokamapEditor/ux/AreaSidebar/Field';
import HelpTip                        from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import currentEditorStore             from '@/TokamapEditor/stores/currentEditorStore';
import { commonValue }                from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import { canHaveRailing }             from '@/TokamapEditor/helpers/surface';
import { canHaveSurface }             from '@/TokamapEditor/helpers/surface';
import { surfaceBlockedByImage }      from '@/TokamapEditor/helpers/surface';
import { NONE }                       from '@/TokamapEditor/helpers/surface';
import { MATERIAL_LABELS }            from '@/TokamapEditor/helpers/surface';
import { SCATTER_HINTS }              from '@/TokamapEditor/helpers/surface';
import { SCALE_OPTIONS }              from '@/TokamapEditor/helpers/surface';
import { SCATTER_OPTIONS }            from '@/TokamapEditor/helpers/surface';
import { TINT_OPTIONS }               from '@/TokamapEditor/helpers/surface';
import { BORDER_LABELS }              from '@/TokamapEditor/helpers/surface';
import { RAILING_LABELS }             from '@/TokamapEditor/helpers/surface';
import { SHOW_DECOR }                 from '@/TokamapEditor/ux/constants';
import useDesignTokens                from '@/hooks/useDesignTokens';

const THUMBNAIL_PX = 96;

type Scatter = SurfaceScatterDensity | typeof NONE;

type GroundTab = 'surface' | 'border' | 'railing';

const GroundButton = observer(({ shapes }: { shapes: Shape[]; }) => {
	const tokens = useDesignTokens();
	const threeD = currentEditorStore.get.getAllowPitch();
	const surfaced = canHaveSurface(shapes);
	const railed = SHOW_DECOR && threeD && canHaveRailing(shapes);
	const tabs: { value: GroundTab; label: string; }[] = [
		...(surfaced ? [{ value: 'surface' as const, label: 'Sol' }, { value: 'border' as const, label: 'Bordure' }] : []),
		...(railed ? [{ value: 'railing' as const, label: 'Barrière' }] : []),
	];
	const [tab, setTab] = React.useState<GroundTab>(tabs[0]?.value ?? 'surface');

	if (!tabs.length) {
		if (!surfaceBlockedByImage(shapes)) return null;

		return (
			<SettingsButton
				label="Aménagement"
				summary="Forme avec image"
				unavailable="Sol, bordure et barrière ne s'appliquent pas à une forme qui porte une image. Retirez l'image dans l'onglet Contenu pour les utiliser."
				preview={<span style={{ width: 24, height: 24, borderRadius: 5, flexShrink: 0, border: `1px solid ${tokens.border.default}`, background: tokens.fill.tertiary }} />}
			/>
		);
	}

	const current = tabs.some(t => t.value === tab) ? tab : tabs[0]!.value;

	const materials = new Set(shapes.map(shape => shape.surface?.material ?? null));
	const borders = new Set(shapes.map(shape => shape.surfaceBorder ?? NONE));
	const railings = new Set(shapes.map(shape => shape.railing ?? NONE));
	const mixed = (surfaced && (materials.size > 1 || borders.size > 1)) || (railed && railings.size > 1);

	const material = surfaced ? [...materials][0] : null;
	const border = surfaced ? [...borders][0] : NONE;
	const railing = railed ? [...railings][0] : NONE;
	const parts = [
		material && MATERIAL_LABELS[material],
		border && border !== NONE && `Bordure ${BORDER_LABELS[border as SurfaceBorder].toLowerCase()}`,
		railing && railing !== NONE && RAILING_LABELS[railing as RailingStyle],
	].filter(Boolean);

	const bgColor = commonValue(shapes, shape => shape.style?.bgColor);
	const image = material ? surfacePreviewUrl(material, 48) : undefined;

	return (
		<SettingsButton
			label="Aménagement"
			title={<>Aménagement<HelpTip>Sol : une matière à la place du fond. Bordure : un liseré à l'intérieur du contour. Barrière : elle suit le contour quand la carte est inclinée, au bord d'une zone creusée, sur le toit d'un volume, au sol sinon.{!threeD && ' Barrière et habillage demandent la 3D, à activer dans les réglages de la carte.'}</HelpTip></>}
			summary={mixed ? 'Plusieurs valeurs' : (parts.join(' · ') || 'Aucun')}
			mixed={mixed}
			width={300}
			preview={(
				<span
					style={{
						width: 24,
						height: 24,
						borderRadius: 5,
						flexShrink: 0,
						border: `1px solid ${tokens.border.default}`,
						background: image ? `center / cover no-repeat url(${image})` : (bgColor ?? tokens.fill.tertiary),
					}}
				/>
			)}
		>
			{tabs.length > 1 && <Segmented block value={current} options={tabs} onChange={value => setTab(value as GroundTab)} />}

			{current === 'surface' && <SurfacePanel shapes={shapes} threeD={threeD} />}
			{current === 'border' && <BorderPanel shapes={shapes} />}
			{current === 'railing' && <RailingPanel shapes={shapes} />}
		</SettingsButton>
	);
});

const SurfacePanel = observer(({ shapes, threeD }: { shapes: Shape[]; threeD: boolean; }) => {
	const editor = currentEditorStore.get;
	const ids = shapes.map(shape => shape.id);
	const materials = new Set(shapes.map(shape => shape.surface?.material ?? null));
	const material = materials.size === 1 ? [...materials][0] : undefined;
	const scale = commonValue(shapes, shape => shape.surface?.scale ?? 'normal');
	const tinted = commonValue(shapes, shape => !!shape.surface?.tinted);
	const scatter = commonValue<Scatter>(shapes, shape => shape.surface?.scatter ?? NONE);
	const bgColor = commonValue(shapes, shape => shape.style?.bgColor);

	const surfaceOf = (next: Required<Omit<ShapeSurface, 'scatter'>> & { scatter: Scatter; }): ShapeSurface => ({
		material: next.material,
		...(next.scale !== 'normal' ? { scale: next.scale } : {}),
		...(next.tinted ? { tinted: true } : {}),
		...(next.scatter !== NONE && SCATTERED_MATERIALS.includes(next.material) ? { scatter: next.scatter } : {}),
	});
	const current = (chosen: SurfaceMaterial) => ({ material: chosen, scale: scale ?? 'normal', tinted: !!tinted, scatter: scatter ?? NONE });
	const apply = (surface: ShapeSurface | undefined) => editor.setShapeSurface(ids, surface);
	const withMaterial = (patch: Partial<ReturnType<typeof current>>) => {
		if (material) apply(surfaceOf({ ...current(material), ...patch }));
	};
	const preview = (id: SurfaceMaterial, size: number) => surfacePreviewUrl(id, size, tinted ? bgColor : undefined);

	return (
		<View gap="sm">
			<View style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 6 }}>
				<SurfaceTile label="Unie" selected={material === null} onClick={() => apply(undefined)} swatch={bgColor} />
				{SURFACE_MATERIALS.map(id => (
					<SurfaceTile
						key={id}
						label={MATERIAL_LABELS[id]}
						selected={material === id}
						onClick={() => apply(surfaceOf(current(id)))}
						thumbnail={preview(id, THUMBNAIL_PX)}
					/>
				))}
			</View>

			{!!material && (
				<>
					<Field label="Taille du motif">
						<Segmented size="small" block value={scale} options={SCALE_OPTIONS} onChange={value => withMaterial({ scale: value as SurfaceScale })} />
					</Field>

					<Field label={<>Couleur<HelpTip>« Du fond » teinte la matière avec la couleur de fond de la forme, en gardant son motif.</HelpTip></>}>
						<Segmented
							size="small"
							block
							value={tinted === undefined ? undefined : (tinted ? 'tinted' : 'natural')}
							options={TINT_OPTIONS}
							onChange={value => withMaterial({ tinted: value === 'tinted' })}
						/>
					</Field>

					{threeD && <Field label={<>Habillage{SCATTER_HINTS[material] && <FieldLabelSuffix>{SCATTER_HINTS[material]}</FieldLabelSuffix>}<HelpTip>De petits éléments en relief semés sur la matière, visibles de près quand la carte est inclinée. Ils s'effacent en s'éloignant.</HelpTip></>}>
						<Segmented
							size="small"
							block
							disabled={!SCATTERED_MATERIALS.includes(material)}
							value={SCATTERED_MATERIALS.includes(material) ? scatter : NONE}
							options={SCATTER_OPTIONS}
							onChange={value => withMaterial({ scatter: value as Scatter })}
						/>
					</Field>}
				</>
			)}
		</View>
	);
});

const BorderPanel = observer(({ shapes }: { shapes: Shape[]; }) => {
	const editor = currentEditorStore.get;
	const borders = new Set(shapes.map(shape => shape.surfaceBorder ?? NONE));
	const border = borders.size === 1 ? [...borders][0] : undefined;
	const surface = commonValue(shapes, shape => shape.surface);
	const bgColor = commonValue(shapes, shape => shape.style?.bgColor);
	const preview = (id: SurfaceBorder) => surfaceBorderPreviewUrl(id, surface, bgColor) ?? undefined;

	return (
		<OptionList
			selected={border}
			options={[{ value: NONE, label: 'Aucune' }, ...SURFACE_BORDERS.map(id => ({ value: id, label: BORDER_LABELS[id], image: preview(id) }))]}
			onPick={value => editor.setShapeSurfaceBorder(shapes.map(shape => shape.id), value === NONE ? undefined : value as SurfaceBorder)}
		/>
	);
});

const RailingPanel = observer(({ shapes }: { shapes: Shape[]; }) => {
	const editor = currentEditorStore.get;
	const styles = new Set(shapes.map(shape => shape.railing ?? NONE));
	const railing = styles.size === 1 ? [...styles][0] : undefined;

	return (
		<OptionList
			selected={railing}
			options={[{ value: NONE, label: 'Aucune' }, ...RAILING_STYLES.map(id => ({ value: id, label: RAILING_LABELS[id], image: railingPreviewUrl(id) }))]}
			onPick={value => editor.setShapeRailing(shapes.map(shape => shape.id), value === NONE ? undefined : value as RailingStyle)}
		/>
	);
});

function OptionList({ options, selected, onPick }: {
	options: { value: string; label: string; image?: string; }[];
	selected?: string;
	onPick: (value: string) => void;
}) {
	const tokens = useDesignTokens();

	return (
		<View gap={4}>
			{options.map(option => {
				const active = option.value === selected;

				return (
					<button
						key={option.value}
						type="button"
						aria-pressed={active}
						onClick={() => onPick(option.value)}
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 10,
							padding: 6,
							borderRadius: 8,
							cursor: 'pointer',
							textAlign: 'left',
							fontSize: 13,
							color: tokens.text.default,
							border: `1px solid ${active ? tokens.color.primary : 'transparent'}`,
							background: active ? `color-mix(in srgb, ${tokens.color.primary} 6%, ${tokens.surface.elevated})` : 'transparent',
						}}
					>
						<span
							style={{
								width: 72,
								height: 24,
								borderRadius: 4,
								flexShrink: 0,
								border: `1px solid ${tokens.border.default}`,
								background: option.image ? `center / cover no-repeat url(${option.image})` : tokens.fill.tertiary,
							}}
						/>
						{option.label}
					</button>
				);
			})}
		</View>
	);
}

function SurfaceTile({ label, selected, onClick, thumbnail, swatch }: {
	label: string;
	selected: boolean;
	onClick: () => void;
	thumbnail?: string;
	swatch?: string;
}) {
	const tokens = useDesignTokens();

	return (
		<View gap={2} pointer onClick={onClick} style={{ minWidth: 0 }}>
			<View
				br={6}
				style={{
					aspectRatio: '1',
					overflow: 'hidden',
					border: `1px solid ${selected ? tokens.color.primary : tokens.border.default}`,
					outline: selected ? `1px solid ${tokens.color.primary}` : undefined,
					background: swatch ?? tokens.fill.tertiary,
				}}
			>
				{thumbnail && <img src={thumbnail} alt="" style={{ width: '100%', height: '100%', display: 'block' }} />}
			</View>

			<View
				size={11}
				color={selected ? tokens.color.primary : tokens.text.muted}
				style={{ textAlign: 'center', lineHeight: 1.15 }}
			>
				{label}
			</View>
		</View>
	);
}

export default GroundButton;
